/**
 * permissionManagerClient — management-plane calls to CMHKAgentPlatformPermissionManager
 * through the Gateway's `/permission/*` relay.
 *
 * The PM API is SigV4-only and its resource policy admits only the Gateway task
 * role, so the browser never talks to it directly: the Gateway authenticates our
 * Federate id_token (X-Id-Token, see context/gatewayAuthHeaders.ts), signs the
 * upstream call with its own role and re-attaches the verified id_token so the
 * PM service resolves the real end user for `manage` authorization.
 *
 * Paths and shapes mirror CMHKAgentPlatformPermissionManagerModel (restJson1,
 * camelCase keys, timestamps as epoch seconds):
 *   GET  /permission/resources?resourceType=agent[&action=manage]
 *   GET  /permission/resources/{resourceType}/{resourceId}/grants
 *   POST /permission/grants           (PutGrant  — create or replace, body only)
 *   POST /permission/grants/delete    (DeleteGrant — idempotent, body only)
 *
 * Errors arrive in one of two envelopes and are normalised to PermissionApiError:
 *   Gateway (401/403/502):  { error: { code, message } }
 *   PM service (4xx/5xx):   { message, ... }   e.g. AccessDeniedException = 403
 */
import { appConfig } from '../config';
import { refreshAuth } from '../context/AuthContext';
import { gatewayAuthHeaders } from '../context/gatewayAuthHeaders';

const API_BASE = `${appConfig.gatewayUrl}/permission`;

/** Only `agent` exists in v1; the enum grows server-side. */
export type ResourceType = 'agent';
/** `manage` implies `invoke` server-side. */
export type Action = 'invoke' | 'manage';
export type PrincipalType = 'team' | 'user' | 'posix' | 'ldap';

export interface Grant {
  resourceType: ResourceType;
  resourceId: string;
  principalType: PrincipalType;
  principalId: string;
  /** Display label; equals principalId when the server knows no name. */
  principalName: string;
  actions: Action[];
  grantedBy: string;
  /** ISO-8601, or '' when the server omitted it. */
  grantedAt: string;
  expiresAt?: string;
  note?: string;
}

export interface AuthorizedResource {
  resourceType: ResourceType;
  resourceId: string;
  actions: Action[];
}

export class PermissionApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'PermissionApiError';
  }
}

// ── Wire shapes (what the service actually returns) ────────────────────────────

interface WireGrant {
  resourceType?: string;
  resourceId?: string;
  principalType?: string;
  principalId?: string;
  principalName?: string;
  actions?: string[];
  grantedBy?: string;
  grantedAt?: number | string;
  expiresAt?: number | string;
  note?: string;
}

/** restJson1 body timestamps are epoch seconds; tolerate an ISO string too. */
const toIso = (v: number | string | undefined): string | undefined => {
  if (v == null) return undefined;
  if (typeof v === 'number') return new Date(v * 1000).toISOString();
  return v;
};

const fromWireGrant = (g: WireGrant): Grant => ({
  resourceType: (g.resourceType ?? 'agent') as ResourceType,
  resourceId: g.resourceId ?? '',
  principalType: (g.principalType ?? 'user') as PrincipalType,
  principalId: g.principalId ?? '',
  principalName: g.principalName || g.principalId || '',
  actions: (g.actions ?? []) as Action[],
  grantedBy: g.grantedBy ?? '',
  grantedAt: toIso(g.grantedAt) ?? '',
  expiresAt: toIso(g.expiresAt),
  note: g.note,
});

// ── Transport ─────────────────────────────────────────────────────────────────

async function request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const doFetch = () => {
    const headers = gatewayAuthHeaders(body !== undefined ? { 'Content-Type': 'application/json' } : {});
    return fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
  };

  let resp = await doFetch();

  // Expired id_token → silent Federate refresh + one retry (mirrors GatewayClient).
  if (resp.status === 401) {
    const newToken = await refreshAuth();
    if (newToken) resp = await doFetch();
    if (resp.status === 401) throw new PermissionApiError(401, 'unauthorized', 'Session expired');
  }

  if (!resp.ok) {
    let code = 'unknown';
    let message = `HTTP ${resp.status}`;
    try {
      const err = (await resp.json()) as {
        error?: { code?: string; message?: string };
        message?: string;
      };
      if (err?.error) {
        // Gateway envelope (auth failure, relay rejection, upstream failure).
        code = err.error.code ?? code;
        message = err.error.message ?? message;
      } else if (typeof err?.message === 'string') {
        // PM service (restJson1) error body.
        code = resp.status === 403 ? 'access_denied' : `http_${resp.status}`;
        message = err.message;
      }
    } catch {
      /* non-JSON error body: keep the HTTP status message */
    }
    throw new PermissionApiError(resp.status, code, message);
  }

  // DeleteGrant returns an empty structure; tolerate an empty body.
  const text = await resp.text();
  return (text ? JSON.parse(text) : {}) as T;
}

const query = (params: object): string => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, string | number | undefined>)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
};

// ── Operations ────────────────────────────────────────────────────────────────

export interface ListGrantsParams {
  resourceType: ResourceType;
  resourceId: string;
  principalType?: PrincipalType;
  action?: Action;
  maxResults?: number;
  nextToken?: string;
}

async function listGrants(params: ListGrantsParams): Promise<{ grants: Grant[]; nextToken?: string }> {
  const { resourceType, resourceId, ...rest } = params;
  const path =
    `/resources/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}/grants` +
    query(rest);
  const data = await request<{ grants?: WireGrant[]; nextToken?: string }>('GET', path);
  return { grants: (data.grants ?? []).map(fromWireGrant), nextToken: data.nextToken };
}

/**
 * Every grant on a resource. Pages through `nextToken` so a resource with more
 * grants than one page never hides rows that then could not be revoked here.
 */
async function listAllGrants(params: Omit<ListGrantsParams, 'nextToken'>): Promise<Grant[]> {
  const all: Grant[] = [];
  let nextToken: string | undefined;
  do {
    const page = await listGrants({ ...params, maxResults: params.maxResults ?? 500, nextToken });
    all.push(...page.grants);
    nextToken = page.nextToken;
  } while (nextToken);
  return all;
}

export interface ListAuthorizedResourcesParams {
  resourceType: ResourceType;
  action?: Action;
  maxResults?: number;
  nextToken?: string;
}

async function listAuthorizedResources(
  params: ListAuthorizedResourcesParams,
): Promise<{ resources: AuthorizedResource[]; nextToken?: string }> {
  const data = await request<{ resources?: AuthorizedResource[]; nextToken?: string }>(
    'GET',
    `/resources${query(params)}`,
  );
  return { resources: data.resources ?? [], nextToken: data.nextToken };
}

export interface PutGrantParams {
  resourceType: ResourceType;
  resourceId: string;
  principalType: PrincipalType;
  principalId: string;
  /** Non-empty; `manage` implies `invoke`. */
  actions: Action[];
  principalName?: string;
  note?: string;
}

async function putGrant(params: PutGrantParams): Promise<{ grant: Grant; propagationNote: string }> {
  const body: Record<string, unknown> = {
    resourceType: params.resourceType,
    resourceId: params.resourceId,
    principalType: params.principalType,
    principalId: params.principalId,
    actions: params.actions,
  };
  if (params.principalName) body.principalName = params.principalName;
  if (params.note) body.note = params.note;
  const data = await request<{ grant?: WireGrant; propagationNote?: string }>('POST', '/grants', body);
  return { grant: fromWireGrant(data.grant ?? {}), propagationNote: data.propagationNote ?? '' };
}

export interface DeleteGrantParams {
  resourceType: ResourceType;
  resourceId: string;
  principalType: PrincipalType;
  principalId: string;
}

async function deleteGrant(params: DeleteGrantParams): Promise<void> {
  await request<Record<string, never>>('POST', '/grants/delete', params);
}

export const permissionManagerClient = {
  listGrants,
  listAllGrants,
  listAuthorizedResources,
  putGrant,
  deleteGrant,
} as const;
